import { DateTime } from "luxon";
import { filter, groupBy, isEmpty as lodashIsEmpty, isNull as lodashIsNull, isEmpty, isNil } from "lodash";
import moment from "moment";
import { cstMoment } from "@common/utils/date.utils";
import { triggerShare } from "@/helpers/triggerShare";
import { createChallenge } from "@/services/ChallengeService";
import config from "@/config";

/**
 * Unmasks a masked value by removing leading dollar sign ($) and comma (,) characters.
 *
 * @param maskedValue - The masked value to be unmasked.
 * @returns The unmasked value.
 */
export const unmaskValue = (maskedValue: string | number): string => {
    if (typeof maskedValue === "number") {
        maskedValue = maskedValue.toFixed(0);
    }
    return (maskedValue as string).replace(/^\$|,/g, "");
};

/**
 * Returns the day number based on the given date.
 * If no date is given, use the current date.
 * (0 - Monday, ... 6 - Sunday)
 *
 * @param dateStr - The date string or Date object.
 * @returns The day number of the week.
 */
export const getDayNumber = (dateStr?: string | Date): number => {
    let date = dateStr ? (dateStr instanceof Date ? dateStr : new Date(dateStr)) : new Date();
    const intDayNumber = date.getDay() - 1;
    return intDayNumber >= 0 ? intDayNumber : 6;
};

/**
 * Checks if value is 0
 * @param val - The value to be checked.
 */
export const isZero = (val: any): boolean => {
    if (!val) return true;
    if (isNaN(val)) return true;
    const parsed = parseInt(val);
    if (!parsed) return true;
    return false;
};

/**
 * Formats rank
 * @param rank - The rank query response.
 */
export const getRank = (rank: { currentRank?: number; totalRank?: number }): string => {
    if (rank.currentRank && rank.totalRank) {
        return `${rank.currentRank} / ${rank.totalRank}`;
    }
    return "No rankings yet.";
};

/**
 * Utility function to get the start and end dates of the week that the given date falls in.
 *
 * @param date - The date string in the format 'M/d/yyyy' for which to find the week's start and end dates.
 * @returns An object containing the start and end dates of the week.
 * @property startOfWeek - The start date of the week in the format 'yyyy-MM-dd'.
 * @property endOfWeek - The end date of the week in the format 'yyyy-MM-dd'.
 */
export const getStartAndEndOfWeek = (date: string) => {
    const currentDate = DateTime.fromFormat(date, "M/d/yyyy");
    const startOfWeek = new Date(currentDate.startOf("week") as any).toLocaleDateString();
    const endOfWeek = new Date(currentDate.endOf("week") as any).toLocaleDateString();
    return { startOfWeek, endOfWeek };
};

export const groupScoresByDate = (scores: Array<{ createdAt: string }>) => {
    return Object.entries(
        groupBy(
            scores?.map((el) => ({
                ...el,
                createdAt: cstMoment(el.createdAt).format("M/DD/YYYY"),
            })),
            (el) => (el as any).createdAt,
        ),
    )?.map((el) => ({ period: el[0], dayName: moment(el[0]).format("d"), scores: el[1] }));
};

/**
 * Masks a string by revealing the first `showCharacterIndex` characters and masking the rest with asterisks (*).
 *
 * @param inputString - The input string to be masked.
 * @param showCharacterIndex - The index up to which characters should be revealed.
 * @returns The masked string.
 */
export const maskString = (inputString: string, showCharacterIndex: number): string => {
    // Check if the input string has at least two characters
    if (inputString.length < 2) return "**";

    // Extract the first two characters from the input string
    const firstTwoChars = inputString.slice(0, showCharacterIndex);

    // Create a string of asterisks with the same length as the original string
    const maskedChars = "*".repeat(inputString.length - showCharacterIndex);

    // Concatenate the first two characters with the masked characters
    return firstTwoChars + maskedChars;
};

/**
 * Filters an array of guest plays to find plays that occurred on the current date
 * and in the current area of the user.
 *
 * @param guestPlay - An array of guest play data. If empty or not provided, reads from localStorage.
 * @param userState - The user's state, which includes the current area.
 * @returns An array of guest plays that match the criteria.
 */
export const getGuestPlaysToday = (guestPlay: any[] = [], userState: { currentAreaId?: string } = {}) => {
    // If guestPlay is empty or not provided, read from localStorage
    const playsToFilter = isEmpty(guestPlay) 
        ? (JSON.parse(localStorage.getItem("guest_gameplay") || "[]") as any[])
        : guestPlay;
    
    // Read guest selected area_id from localStorage
    const guestAreaId = localStorage.getItem("guest_area_id") || userState?.currentAreaId;
    
    return filter(playsToFilter, (gameplay: any) => {
        const createdAt = gameplay.created_at || gameplay.createdAt;
        const areaId = gameplay.area_id || gameplay.areaId;

        // Use moment to compare only the calendar date
        const isSameDate = createdAt ? moment(createdAt).isSame(moment(), "day") : false;
        const isSameArea = areaId === guestAreaId;
        
        return isSameDate && isSameArea;
    });
};

/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
 *
 * @returns An object containing the mobile OS type - Android/IOS
 */
export const getMobileOperatingSystem = () => {
    const os = { isAndroid: false, isIOS: false };
    const userAgent = window.navigator.userAgent || (window.navigator as any).vendor || (window as any).opera;

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) return { ...os, isAndroid: true };
    if (/android/i.test(userAgent)) return { ...os, isAndroid: true };

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) return { ...os, isIOS: true };
    return { ...os, isAndroid: true };
};

/**
 * Filters an object of empty property values
 *
 * @param payload - The object to be filtered.
 * @returns The filtered object.
 */
export const filterEmptyProps = (payload: Record<string, any>) => {
    return Object.fromEntries(
        Object.entries(payload)
            ?.map((entry) => {
                if (!entry[1]) return null as any;
                return entry as any;
            })
            ?.filter((entry) => entry),
    );
};

/**
 * Duplicates array elements based on specified count
 *
 * @param data - The data to be duplicated
 * @param count - The number of data to be duplicated
 * @returns The duplicated data
 */
export const dataDuplicater = ({ data = [], count = 10 }: { data: any[]; count?: number }) => {
    if (data?.length >= 10) return data;
    else if (data?.length === 1) return data;
    else if (!data?.length) return [];

    const fakedData: any[] = [];
    for (let i = 0; i < count; i++) {
        if (fakedData?.length >= 10) break;
        let sliced = data?.slice(0, data?.length + 1);
        if (count - (fakedData?.length + 1) < sliced?.length + 1) {
            sliced = data?.slice(0, count - fakedData?.length);
        }
        sliced?.map((slice: any) => fakedData.push(slice));
    }
    return fakedData;
};

/**
 * Converts Kilometers to Miles
 *
 * @param km - value in km
 * @returns converted km to mi
 */
export const convertKmToMiles = (km?: number): number => {
    if (isNaN(km as any) || !km) return 0;
    return km * 0.621371;
};

/**
 * Shorten number to thousands, millions, billions, etc.
 * http://en.wikipedia.org/wiki/Metric_prefix
 *
 * @param num - Number to shorten.
 * @param digits - The number of digits to appear after the decimal point.
 * @returns string | number
 */
export const shortenLargeNumber = (num: number, digits: number) => {
    const units = ["k", "m", "g", "t", "p", "e", "z", "y"];
    let decimal;
    for (let i = units.length - 1; i >= 0; i--) {
        decimal = Math.pow(1000, i + 1);
        if (num <= -decimal || num >= decimal) {
            return +(num / decimal).toFixed(digits) + (units as any)[i];
        }
    }
    return num;
};

/**
 * Returns the ordinal suffix for a given number.
 *
 * @param number - The input number for which to determine the ordinal suffix.
 * @returns The ordinal suffix ("st", "nd", "rd", or "th").
 */
export const nthNumber = (number: number): string => {
    if (number > 3 && number < 21) return `${number}th`;
    switch (number % 10) {
        case 1:
            return `${number}st`;
        case 2:
            return `${number}nd`;
        case 3:
            return `${number}rd`;
        default:
            return `${number}th`;
    }
};

/**
 * Handles parsing of guess, converts it to integer
 * @param guess - guess string
 * @returns parsed guess
 */
export const guessParser = (guess?: string | number): number => {
    if (!guess) return 0;
    return parseInt(guess?.toString().substring(1).split(",").join(""));
};

/**
 * Checks if the select location timestamp is from today.
 *
 * @returns True if the select location timestamp is from today, false otherwise.
 */
export const hasSelectLocationTimestampToday = () => {
    const selectLocationTimestamp = localStorage.getItem("select_location_timestamp");
    let redirectToDailyProperties = false;

    if (!lodashIsEmpty(selectLocationTimestamp) || !lodashIsNull(selectLocationTimestamp)) {
        const selectLocationTimestampDiff = cstMoment()
            .startOf("day")
            .diff(cstMoment(selectLocationTimestamp).startOf("day"), "days", false);

        redirectToDailyProperties = selectLocationTimestampDiff === 0;
    }
    return redirectToDailyProperties;
};

/**
 * Checks if a guest user should be prompted to select a location.
 * A user needs to select a location if they have no current location
 * or if they haven't selected a location today.
 *
 * @param userState - The user state object containing currentLocation.
 * @returns True if the user should be prompted to select a location, false otherwise.
 */
export const shouldPromptGuestLocationSelection = (
    userState: { currentLocation?: string | null } | null | undefined,
): boolean => {
    const hasNoCurrentLocation = isEmpty(userState?.currentLocation) || isNil(userState?.currentLocation);
    return hasNoCurrentLocation || !hasSelectLocationTimestampToday();
};

/**
 * Creates a challenge link and triggers sharing on web and mobile platforms.
 *
 * @param score_id - The ID of the score associated with the challenge.
 * @param score - The score to include in the shared text.
 * @returns A promise that resolves once the challenge link is created and sharing is triggered.
 */
export const createChallengeLink = async (score_id: string, score: number) => {
    const challenge = await createChallenge({ challenger_score_id: score_id });
    const challengeId = (challenge as any)?.data?.id;
    await triggerShare({
        web: {
            text: `${config.BASE_URL}/challenge/${challengeId}`,
        },
        mobile: {
            title: "PriceMe",
            text: `I just scored ${score} pts guessing the price of this home on PriceMe 🎈

Think you can beat my score?

Tap^ to guess what it sold for. Expires at midnight.`,
            url: `${config.BASE_URL}/challenge/${challengeId}`,
        },
    });
};

/**
 * Formats a price to USD currency string.
 *
 * @param price - The price to format.
 * @returns The formatted price string.
 */
export const formatPrice = (price: number) =>
    Number(price).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    });

/**
 * Checks if the stored timestamp is from the same day as the current day in the "America/Chicago" timezone.
 *
 * @returns True if the timestamp is from the same day as today, false otherwise.
 */
export const isSameDayAsTimestamp = () => {
    const timestamp = localStorage.getItem("select_location_timestamp");
    if (!timestamp) return false;
    const timestampDayStart = cstMoment(timestamp).startOf("day");
    const currentDayStart = cstMoment().startOf("day");
    return (timestampDayStart as any).isSame(currentDayStart);
};

/**
 * Converts a pixel (px) value to rem units, based on a specified or default base rem value.
 *
 * - If no base rem value is provided, it defaults to the root `<html>` font size.
 * - The function accepts `pxValue` as either a string with "px" suffix (e.g., "16px") or as a number (e.g., 16).
 *
 * @param pxValue - The pixel value to convert. Can be a number (e.g., 16) or a string with "px" (e.g., "16px").
 * @param baseRemValue - The base rem value for conversion. Defaults to the root font size.
 * @returns The equivalent value in rem units.
 *
 * @example
 * // Assuming the base font size is 16px:
 * pxToRem(16);       // returns 1
 * pxToRem("32px");   // returns 2
 */
export const pxToRem = (
    pxValue: number | string,
    baseRemValue: number = parseFloat(getComputedStyle(document.documentElement).fontSize),
) => {
    const tempPx = `${pxValue}`.replace("px", "");
    return (1 / baseRemValue) * parseInt(tempPx);
};

/**
 * Pauses the execution for a specified duration.
 *
 * @param ms - The duration to wait in milliseconds.
 * @returns A promise that resolves after the specified duration.
 */
export const wait = (ms: number = 1000) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Normalizes API responses that may return arrays under different wrappers.
 * Accepts either the full react-query result.data object or raw payloads.
 * Returns a consistent array or an empty array.
 */
export function normalizeDailyArray(source: any): any[] {
    const root = (source as any)?.data ?? source;
    if (Array.isArray(root)) return root;
    if (Array.isArray((root as any)?.properties)) return (root as any).properties;
    if (Array.isArray((root as any)?.data)) return (root as any).data;
    return [];
}

export const normalizeMlsId = (value?: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (trimmed.length === 0) return undefined;

    const upper = trimmed.toUpperCase();
    return upper.startsWith("MRD") ? upper : `MRD${upper}`;
};

/**
 * Function to validate image URLs
 *
 * @param url - The image url to be checked.
 * @returns A promise that resolves an object for the valid url.
 */
export const validateImageURL = (url: string) => {
    return new Promise<{ url: string; isValid: boolean }>((resolve) => {
        const img = new Image();
        const timeout = 5000; // 5 second timeout

        const timer = setTimeout(() => {
            img.src = ""; // Cancel the image load
            resolve({ url, isValid: false });
        }, timeout);

        img.onload = () => {
            clearTimeout(timer);
            resolve({ url, isValid: true });
        };

        img.onerror = () => {
            clearTimeout(timer);
            resolve({ url, isValid: false });
        };

        img.src = url;
    });
};

// Returns the display price: sold/sold_price if closed, otherwise list/list_price
export function getDisplayPrice(property: any) {
    if (!property) return 0;

    const statusRaw = property.statusIndicator ?? property.status_indicator ?? "";
    const status = String(statusRaw).toLowerCase();
    const sold = property.soldPrice ?? property.sold_price ?? 0;
    const list = property.listPrice ?? property.list_price ?? 0;

    const parsePrice = (value: unknown) => {
        if (typeof value === "number") return Number.isFinite(value) ? Math.round(value) : 0;
        if (typeof value !== "string") return 0;
        const normalized = value.replace(/[^0-9.-]/g, "");
        if (!normalized) return 0;
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? Math.round(parsed) : 0;
    };

    const soldNum = parsePrice(sold);
    const listNum = parsePrice(list);

    if (status === "closed" && soldNum) {
        return soldNum;
    }
    return listNum;
}

/**
 * Transforms a visitor status string to the standardized format for analytics.
 * - 'new' => 'new-guest'
 * - 'repeat' => 'repeat-guest'
 * - otherwise returns the input unchanged
 *
 * @param visitorStatus - The original visitor status (e.g., 'new', 'repeat', 'new-guest', 'registered').
 * @returns The transformed visitor status.
 */
export function transformVisitorStatus(visitorStatus: string) {
    if (visitorStatus === "new") return "new-guest";
    if (visitorStatus === "repeat") return "repeat-guest";
    return visitorStatus;
}

/**
 * Encodes a property address for safe use in a URL segment.
 * Replaces spaces with dashes and encodes special characters.
 *
 * @param address - The address to encode.
 * @returns The encoded address string.
 */
export function encodeAddressForUrl(address: string) {
    if (!address) return "";
    return encodeURIComponent(address.replace(/\s+/g, "-"));
}
